<template>
  <div>
    <!-- Loader -->
    <loading-panel :loading="loading"></loading-panel>

    <!-- Hero Section for Empty State -->
    <card v-if="isBrandNewGroup && !loading && !automationLoading" >
      <div class="hero-container">
        <div class="hero-content">
          <div class="hero-label">Enhanced Email Communications</div>
          <h1 class="hero-title">Email Templates & Automations</h1>
          <h2 class="hero-subtitle">Create professional, automated email communications for your business</h2>
          <p class="hero-description">
            Email Templates is a visual email builder that helps businesses create transactional emails, customer communications, marketing campaigns, and automated workflows with drag-and-drop simplicity and powerful customization.
          </p>
          <div class="hero-actions">
            <base-button type="primary" size="lg" @click="startWalkthrough()">
              <i class="tim-icons icon-simple-add mr-2"></i>
              Get started
            </base-button>
            <base-button type="default" size="lg" class="ml-1" @click="showTemplateGallery()">
              <i class="tim-icons icon-book-bookmark mr-2"></i>
              Browse template types
            </base-button>
          </div>
        </div>

        <!-- Demo Interface -->
        <div class="demo-preview">
          <div class="demo-interface-enhanced">
            <!-- Left Sidebar with Components -->
            <div class="demo-sidebar">
              <h4>Components</h4>
              <div class="component-grid">
                <div class="component-item draggable">
                  <i class="tim-icons icon-align-left-2"></i>
                  <span>Text</span>
                </div>
                <div class="component-item">
                  <i class="tim-icons icon-image-02"></i>
                  <span>Image</span>
                </div>
                <div class="component-item">
                  <i class="tim-icons icon-button-power"></i>
                  <span>Button</span>
                </div>
                <div class="component-item">
                  <i class="tim-icons icon-minimal-right"></i>
                  <span>Divider</span>
                </div>
                <div class="component-item">
                  <i class="tim-icons icon-spaceship"></i>
                  <span>Social</span>
                </div>
                <div class="component-item">
                  <i class="tim-icons icon-chart-pie-36"></i>
                  <span>Chart</span>
                </div>
              </div>

              <!-- Feature Highlight -->
              <div class="feature-highlight">
                <div class="highlight-icon">
                  <i class="tim-icons icon-settings-gear-63"></i>
                </div>
                <div class="highlight-text">
                  <h5>Smart Variables</h5>
                  <p>Auto-populate customer data</p>
                </div>
              </div>
            </div>

            <!-- Main Canvas Area -->
            <div class="demo-canvas-enhanced">
              <div class="canvas-header">
                <div class="canvas-tabs">
                  <span class="tab active">Design</span>
                  <span class="tab">Preview</span>
                  <span class="tab">Code</span>
                </div>
                <div class="canvas-tools">
                  <i class="tim-icons icon-tablet-2"></i>
                  <i class="tim-icons icon-mobile"></i>
                </div>
              </div>

              <div class="canvas-content">
                <!-- Animated Demo Section -->
                <div class="demo-animation-area">
                  <div class="drag-demo">
                    <div class="component-source">
                      <div class="component-block floating">
                        <i class="tim-icons icon-align-left-2"></i>
                        Text Block
                      </div>
                    </div>
                    <div class="drag-path">
                      <div class="drag-arrow">→</div>
                    </div>
                    <div class="drop-zone">
                      <div class="canvas-preview">
                        <div class="email-structure">
                          <div class="email-header-preview">Header</div>
                          <div class="email-content-preview">
                            <div class="content-block added">
                              <i class="tim-icons icon-align-left-2"></i>
                              Welcome Message
                            </div>
                            <div class="content-block">
                              <i class="tim-icons icon-image-02"></i>
                              Hero Image
                            </div>
                            <div class="drop-indicator"></div>
                          </div>
                          <div class="email-footer-preview">Footer</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Action Hint -->
                <div class="demo-hint">
                  <i class="tim-icons icon-bulb-63"></i>
                  <span>Drag components from the sidebar to build your email template</span>
                </div>
              </div>
            </div>

            <!-- Right Panel with Properties -->
            <div class="demo-properties">
              <h4>Properties</h4>
              <div class="property-group">
                <label>Font Size</label>
                <div class="property-input">
                  <input type="range" min="12" max="24" value="16">
                  <span>16px</span>
                </div>
              </div>
              <div class="property-group">
                <label>Color</label>
                <div class="color-picker">
                  <div class="color-swatch" style="background: #3b82f6"></div>
                  <div class="color-swatch" style="background: #ef4444"></div>
                  <div class="color-swatch" style="background: #10b981"></div>
                  <div class="color-swatch active" style="background: #6366f1"></div>
                </div>
              </div>
              <div class="property-group">
                <label>Alignment</label>
                <div class="alignment-buttons">
                  <button class="align-btn active"><i class="tim-icons icon-align-left-2"></i></button>
                  <button class="align-btn"><i class="tim-icons icon-align-center"></i></button>
                  <button class="align-btn"><i class="tim-icons icon-bullet-list-67"></i></button>
                </div>
              </div>

              <!-- Template Stats -->
              <div class="template-stats">
                <h5>Template Stats</h5>
                <div class="stat-item">
                  <span>Deliverability</span>
                  <div class="stat-bar">
                    <div class="stat-fill" style="width: 94%"></div>
                  </div>
                  <span>94%</span>
                </div>
                <div class="stat-item">
                  <span>Mobile Ready</span>
                  <div class="stat-bar">
                    <div class="stat-fill" style="width: 100%"></div>
                  </div>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </card>

    <!-- Regular List View for Existing Templates/Automations -->
    <div v-else class="templates-list-section">
      <div class="row">
        <div class="col">
          <card>
            <!-- Tab Navigation -->
            <div class="tabs-header d-flex align-items-end mb-3">
              <tab-rail
                :tabs="tabs"
                :value="activeTabPosition"
                @input="onTabRailSelect"
                class="flex-grow-1"
              />
              <div class="tabs-header__action" v-if="activeTabIndex === 0">
                <base-button size="sm" @click="proceedToBuilder()" type="primary">
                  <i class="tim-icons icon-simple-add mr-2"></i> Create email template
                </base-button>
                <el-dropdown>
                  <base-button
                               simple
                               type="primary"
                               link
                  > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                  </base-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a class="dropdown-item mt-2 mb-2" @click.prevent="showTemplateGallery()" href="#">
                        <i class="tim-icons icon-components text-default mr-2"></i> View email template types
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
              <div class="tabs-header__action" v-if="activeTabIndex === 2">
                <base-button size="sm" type="primary" @click="$refs.smsTab && $refs.smsTab.openEditor()">
                  <i class="tim-icons icon-simple-add mr-2"></i> Create SMS template
                </base-button>
              </div>
              <div class="tabs-header__action" v-if="activeTabIndex === 1">
                <base-button size="sm" @click="createNewAutomation()" type="primary">
                  <i class="tim-icons icon-simple-add mr-2"></i> Create automation
                </base-button>
              </div>
            </div>

            <!-- Loading Panel for Automations -->
            <loading-panel v-if="activeTabIndex === 1" :loading="automationLoading"></loading-panel>

            <!-- Filter bar -->
            <!-- SMS templates tab (stable index 2) renders its own surface -->
            <sms-templates-tab v-if="activeTabIndex === 2" ref="smsTab" />

            <template v-if="activeTabIndex !== 2">
            <filter-bar
              :filters="filterSchema"
              :initial-value="filterValues"
              :result-count="totalDisplayRows"
              :per-page="perPage"
              :per-page-options="perPageOptions"
              saved-filters-key="custom-emails"
              @change="onFiltersChanged"
              @update:perPage="onPerPageChange"
            >
              <template #actions>
                <base-button icon class="btn-link text-white" @click="downloadCSV">
                  <i class="tim-icons icon-cloud-download-93"></i>
                </base-button>
              </template>
            </filter-bar>

            <!-- Empty state: none on this tab yet, or filters hide every record -->
            <div v-if="showEmptyState" class="empty-state text-center">
              <img src="/img/pandas/marketing.png" alt="" class="empty-state-img empty-state-img--md" />
              <h3 class="empty-state__title">{{ emptyState.title }}</h3>
              <p class="empty-state__hint">{{ emptyState.hint }}</p>
            </div>

            <!-- Table View -->
            <div v-if="displayData.length > 0" class="d-none d-sm-block">
              <b-table
                striped
                hover
                :items="displayData"
                :fields="tableFields"
              >
                <!-- Template-specific slots -->
                <template v-if="activeTabIndex === 0" v-slot:cell(template_info)="row">
                  <div>
                    <strong>{{ row.item.name }}</strong>
                    <div class="text-muted small">{{ row.item.description }}</div>
                  </div>
                </template>

                <template v-if="activeTabIndex === 0" v-slot:cell(type)="row">
                  <span
                    :class="['badge', 'badge-default']"
                    :style="{ backgroundColor: row.item.type_color, color: '#fff' }"
                  >
                    <i :class="row.item.type_icon" class="mr-1"></i>
                    {{ row.item.type }}
                  </span>
                </template>

                <template v-if="activeTabIndex === 0" v-slot:cell(usage)="row">
                  <div class="small">
                    <div><i class="tim-icons icon-email-85 mr-1"></i>{{ row.item.sent_count || 0 }} sent</div>
                    <div><i class="tim-icons icon-chart-pie-36 mr-1"></i>{{ row.item.open_rate || 0 }}% open rate</div>
                  </div>
                </template>

                <!-- Automation-specific slots -->
                <template v-if="activeTabIndex === 1" v-slot:cell(automation_info)="row">
                  <div>
                    <strong>{{ row.item.name }}</strong>
                    <div class="text-muted small">{{ row.item.description }}</div>
                  </div>
                </template>

                <template v-if="activeTabIndex === 1" v-slot:cell(trigger_type)="row">
                  <span class="badge badge-info">
                    <i :class="getTriggerTypeIcon(row.item.trigger_type)" class="mr-1"></i>
                    {{ row.item.trigger_type?.name || 'Unknown' }}
                  </span>
                </template>

                <template v-if="activeTabIndex === 1" v-slot:cell(steps_count)="row">
                  <div class="small">
                    <i class="tim-icons icon-bullet-list-67 mr-1"></i>{{ row.item.steps_count || 0 }} steps
                  </div>
                </template>

                <!-- Shared slots -->
                <template v-slot:cell(status)="row">
                  <span v-if="activeTabIndex === 0 && row.item.archived" class="badge badge-default">archived</span>
                  <span v-else-if="activeTabIndex === 0" :class="['badge', 'badge-' + getStatusColor(row.item.status)]">
                    {{ row.item.status }}
                  </span>
                  <span v-else :class="['badge', 'badge-' + getAutomationStatusColor(row.item.is_active)]">
                    {{ getAutomationStatusText(row.item.is_active) }}
                  </span>
                </template>

                <template v-slot:cell(last_modified)="row">
                  <div class="small">
                    <div>{{ getLocalDateTime(row.item.updated_at || row.item.created_at) }}</div>
                    <div class="text-muted">by {{ row.item.updated_by_name || row.item.created_by_name }}</div>
                  </div>
                </template>

                <template v-slot:cell(actions)="row">
                  <el-dropdown>
                    <base-button simple type="primary" link>
                      <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                    </base-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <!-- Template actions -->
                        <template v-if="activeTabIndex === 0 && !row.item.archived">
                          <a class="dropdown-item mb-2" :href="'/mail/custom/template/' + row.item.id"
                              target="_blank">
                            <i class="tim-icons icon-pencil text-default mr-2"></i> Edit template
                          </a>
                          <a class="dropdown-item mb-2" @click.prevent="toggleTemplateStatus(row.item)" href="#">
                            <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px"
                                  :class="row.item.status === 'active' ? 'inactive-dot mr-2' : 'active-dot mr-2'"></span>
                            {{ row.item.status === 'active' ? 'Deactivate' : 'Activate' }}
                          </a>
                          <a class="dropdown-item mb-2" @click.prevent="duplicateTemplate(row.item)" href="#">
                            <i class="tim-icons icon-single-copy-04 text-default mr-2"></i> Duplicate
                          </a>
                          <a class="dropdown-item mb-2" @click.prevent="copyToGroupsPrompt(row.item, 'template')" href="#">
                            <i class="tim-icons icon-world text-default mr-2"></i> Copy to other groups
                          </a>
                          <hr>
                          <a class="dropdown-item" @click.prevent="openArchiveDialog(row.item)" href="#">
                            <i class="tim-icons icon-trash-simple text-default mr-2"></i> Archive template
                          </a>
                        </template>

                        <!-- Archived template actions (OTC-3113) -->
                        <template v-else-if="activeTabIndex === 0 && row.item.archived">
                          <a class="dropdown-item" @click.prevent="openRestoreDialog(row.item)" href="#">
                            <i class="tim-icons icon-refresh-02 text-default mr-2"></i> Restore template
                          </a>
                        </template>

                        <!-- Automation actions -->
                        <template v-else-if="activeTabIndex === 1">
                          <a class="dropdown-item mb-2" :href="'/mail/custom/automation/' + row.item.id" target="_blank">
                            <i class="tim-icons icon-pencil text-default mr-2"></i> Edit automation
                          </a>
                          <a class="dropdown-item mb-2" @click.prevent="toggleAutomationStatus(row.item)" href="#">
                            <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px"
                                  :class="row.item.is_active ? 'inactive-dot mr-2' : 'active-dot mr-2'"
                                  class="active-dot mr-2"></span>
                            {{ row.item.is_active ? 'Deactivate' : 'Activate' }}
                          </a>
                          <a class="dropdown-item mb-2" @click.prevent="duplicateAutomation(row.item)" href="#">
                            <i class="tim-icons icon-single-copy-04 text-default mr-2"></i> Duplicate
                          </a>
                          <a class="dropdown-item mb-2" @click.prevent="copyToGroupsPrompt(row.item, 'automation')" href="#">
                            <i class="tim-icons icon-world text-default mr-2"></i> Copy to other groups
                          </a>
<!--                          <a class="dropdown-item" @click.prevent="viewAutomationAnalytics(row.item)" href="#">-->
<!--                            <i class="tim-icons icon-chart-pie-36 text-success mr-2"></i> Analytics-->
<!--                          </a>-->
                          <hr>
                          <a class="dropdown-item" @click.prevent="deleteAutomationPrompt(row.item)" href="#">
                            <i class="tim-icons icon-trash-simple text-default mr-2"></i> Delete automation
                          </a>
                        </template>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </template>
              </b-table>
            </div>

            <!-- Mobile View -->
            <div class="d-block d-sm-none mt-4" v-if="displayData.length > 0">
              <b-table
                striped
                hover
                :items="displayData"
                :fields="[activeTabIndex === 0 ? 'templates' : 'automations']"
              >
                <template :slot="`head(${activeTabIndex === 0 ? 'templates' : 'automations'})`" slot-scope="row">
                  <span>{{ activeTabIndex === 0 ? 'Templates' : 'Automations' }}: {{ totalDisplayRows }}</span>
                </template>

                <template :slot="`cell(${activeTabIndex === 0 ? 'templates' : 'automations'})`" slot-scope="row">
                  <div class="mobile-template-card">
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{{ row.item.name }}</strong>
                        <span v-if="activeTabIndex === 0 && row.item.archived" class="badge badge-default ml-2">archived</span>
                        <span v-else-if="activeTabIndex === 0" :class="['badge', 'badge-' + getStatusColor(row.item.status), 'ml-2']">
                          {{ row.item.status }}
                        </span>
                        <span v-if="activeTabIndex === 0 && !row.item.archived && row.item.status === 'active' && row.item.can_activate === false"
                              class="badge badge-warning ml-2">
                          Unverified from address
                        </span>
                        <span v-if="activeTabIndex === 1" :class="['badge', 'badge-' + getAutomationStatusColor(row.item.is_active), 'ml-2']">
                          {{ getAutomationStatusText(row.item.is_active) }}
                        </span>
                      </div>
                      <el-dropdown>
                        <base-button simple type="primary" link>
                          <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                        </base-button>
                        <template #dropdown>
                          <el-dropdown-menu>
                            <!-- Template actions -->
                            <template v-if="activeTabIndex === 0 && !row.item.archived">
                              <a class="dropdown-item mb-2" :href="'/mail/custom/template/' + row.item.id"
                                 target="_blank">
                                <i class="tim-icons icon-pencil text-default mr-2"></i> Edit template
                              </a>
                              <a class="dropdown-item mb-2" @click.prevent="toggleTemplateStatus(row.item)" href="#">
                                <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px"
                                      :class="row.item.status === 'active' ? 'inactive-dot mr-2' : 'active-dot mr-2'"></span>
                                {{ row.item.status === 'active' ? 'Deactivate' : 'Activate' }}
                              </a>
                              <a class="dropdown-item mb-2" @click.prevent="duplicateTemplate(row.item)" href="#">
                                <i class="tim-icons icon-single-copy-04 text-default mr-2"></i> Duplicate
                              </a>
                              <a class="dropdown-item mb-2" @click.prevent="copyToGroupsPrompt(row.item, 'template')" href="#">
                                <i class="tim-icons icon-world text-default mr-2"></i> Copy to other groups
                              </a>
                              <hr>
                              <a class="dropdown-item" @click.prevent="openArchiveDialog(row.item)" href="#">
                                <i class="tim-icons icon-trash-simple text-default mr-2"></i> Archive template
                              </a>
                            </template>

                            <!-- Archived template actions (OTC-3113) -->
                            <template v-else-if="activeTabIndex === 0 && row.item.archived">
                              <a class="dropdown-item" @click.prevent="openRestoreDialog(row.item)" href="#">
                                <i class="tim-icons icon-refresh-02 text-default mr-2"></i> Restore template
                              </a>
                            </template>

                            <!-- Automation actions -->
                            <template v-else-if="activeTabIndex === 1">
                              <a class="dropdown-item mb-2" :href="'/mail/custom/automation/' + row.item.id" target="_blank">
                                <i class="tim-icons icon-pencil text-default mr-2"></i> Edit automation
                              </a>
                              <a class="dropdown-item mb-2" @click.prevent="toggleAutomationStatus(row.item)" href="#">
                            <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px"
                                  :class="row.item.is_active ? 'inactive-dot mr-2' : 'active-dot mr-2'"
                                  class="active-dot mr-2"></span>
                                {{ row.item.is_active ? 'Deactivate' : 'Activate' }}
                              </a>
                              <a class="dropdown-item mb-2" @click.prevent="duplicateAutomation(row.item)" href="#">
                                <i class="tim-icons icon-single-copy-04 text-default mr-2"></i> Duplicate
                              </a>
                              <a class="dropdown-item mb-2" @click.prevent="copyToGroupsPrompt(row.item, 'automation')" href="#">
                                <i class="tim-icons icon-world text-default mr-2"></i> Copy to other groups
                              </a>
                              <!--                          <a class="dropdown-item" @click.prevent="viewAutomationAnalytics(row.item)" href="#">-->
                              <!--                            <i class="tim-icons icon-chart-pie-36 text-success mr-2"></i> Analytics-->
                              <!--                          </a>-->
                              <hr>
                              <a class="dropdown-item" @click.prevent="deleteAutomationPrompt(row.item)" href="#">
                                <i class="tim-icons icon-trash-simple text-default mr-2"></i> Delete automation
                              </a>
                            </template>
                          </el-dropdown-menu>
                        </template>
                      </el-dropdown>
                    </div>
                    <div class="mt-2">
                      <small class="text-muted">{{ row.item.description }}</small>
                    </div>
                    <div class="mt-2">
                      <small class="text-muted">
                        Updated: {{ getLocalDateTime(row.item.updated_at || row.item.created_at) }}
                      </small>
                    </div>
                  </div>
                </template>
              </b-table>
            </div>

            <!-- Pagination -->
            <b-pagination
              v-if="displayData.length > 0"
              v-model="currentPage"
              :total-rows="totalDisplayRows"
              :per-page="perPage"
              @input="activeTabIndex === 0 ? getTemplates(perPage, getOffset()) : getAutomations(perPage, getOffset())"
            ></b-pagination>
            </template>
          </card>
        </div>
      </div>
    </div>

    <!-- Copy to other groups modal -->
    <modal :show.sync="modals.copy_to_groups"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-lg">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <h4>Copy <span class="text-primary">{{ copyType === 'template' ? selectedTemplate?.name : selectedAutomation?.name }}</span> to other groups</h4>

        <div class="row mb-4" v-if="groups.length > 0">
          <div class="col">
            <base-input label="Select groups" :error="errs.groups">
              <el-select multiple
                         v-model="selectedGroups"
                         class="select-primary"
                         placeholder="Select groups"
                         value-key="id">
                <el-option
                  v-for="(group, i) in groups"
                  class="select-primary"
                  :label="group.name"
                  :value="{ name: group.name, id: group.id }"
                  :key="'G' + i"
                ></el-option>
              </el-select>
            </base-input>
          </div>
        </div>

        <div v-else class="text-center mb-4">
          <p class="text-muted">No other groups available to copy to.</p>
        </div>

        <div class="row text-center">
          <div class="col">
            <base-button type="default" @click="modals.copy_to_groups = false">Cancel</base-button>
            <base-button type="primary" class="ml-2" @click="executeCopy()" :disabled="groups.length === 0">Copy</base-button>
          </div>
        </div>

          </card>
    </modal>

    <base-alert-dialog
      :show="archiveDialog.show"
      title="Archive template"
      variant="danger"
      :message="archiveDialogMessage"
      show-cancel
      confirm-text="Archive"
      confirm-variant="danger"
      :loading="archiveDialog.loading"
      :dismissible="!archiveDialog.loading"
      @confirm="archiveTemplate"
      @cancel="archiveDialog.show = false" />
    <base-alert-dialog
      :show="restoreDialog.show"
      title="Restore template"
      variant="primary"
      :message="restoreDialogMessage"
      show-cancel
      confirm-text="Restore"
      :loading="restoreDialog.loading"
      :dismissible="!restoreDialog.loading"
      @confirm="restoreTemplate"
      @cancel="restoreDialog.show = false" />

  </div>
</template>

<script>
import { BIcon, BPagination, BTable } from "bootstrap-vue";
import { Dropdown, DropdownItem, DropdownMenu } from "element-ui";
import { Badge } from "@/components";
import { API_LOCATION } from "@/api-config";
import TabRail from '@/components/TabRail.vue';
import SMSTemplatesTab from './SMSTemplatesTab.vue';
import { isSmsBetaCompany } from '@/util/smsBeta';
import axios from "axios";
import swal from "sweetalert2";
import { Auth } from "aws-amplify";
import { localDateTime, formatGroupDate, formatGroupTime } from "@/plugins/dates";
import _ from "lodash";

export default {
  name: "CustomEmailLanding",
  components: {
    TabRail,
    'sms-templates-tab': SMSTemplatesTab,
    BIcon,
    BTable,
    BPagination,
    Badge,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
  },
  data() {
    return {
      loading: false,
      templates: [],
      filteredTemplates: [],
      templateTypes: [],

      // Automation-specific data
      automations: [],
      filteredAutomations: [],
      automationTriggerTypes: [],
      automationLoading: false,
      totalAutomationRows: 0,

      // Unfiltered totals for the group (null until known); drive the first-run hero.
      groupTemplateCount: null,
      groupAutomationCount: null,
      // True when the group has archived templates and no active ones.
      groupHasOnlyArchivedTemplates: false,

      // Archive and restore confirms for a template row (OTC-3113)
      archiveDialog: { show: false, loading: false, template: null },
      restoreDialog: { show: false, loading: false, template: null },


      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,

      // Pagination
      currentPage: 1,
      perPage: 10,
      perPageOptions: [5, 10, 25, 50],
      totalRows: 0,

      // UI state
      activeCategory: 'all',
      selectedTemplateType: null,

      // Tab state
      activeTabIndex: 0,
      tabKey: 0,

      // Tab configuration
      // Placeholder only: checkPermissionsAndBuildTabs() rebuilds this at
      // created() with permission filtering and stable `index` values.
      tabs: [],
      smsTemplates: [],
      smsTemplateTypes: [],
      loadingSmsTemplates: false,
      hasTemplatePermission: false,
      hasAutomationPermission: false,

      // Status options for templates
      templateStatusOptions: [
        { value: 'active', label: 'Active' },
        { value: 'draft', label: 'Draft' }
      ],

      // Status options for automations
      automationStatusOptions: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ],

      // Flat filter values for the shared <filter-bar>. Seeded from the URL;
      // saved-views precedence is resolved by FilterBar. template_type = array of
      // type names; created_date = daterange { mode, days, from, to } (range only).
      filterValues: this.buildInitialFilterValues(),
      // FilterBar (saved-views enabled) drives the first fetch via its initial
      // `change`; this guards the page number on that first call.
      didInitialFetch: false,

      // Table fields for templates
      templateTableFields: [
        { key: 'template_info', label: 'Template' },
        { key: 'type', label: 'Type' },
        { key: 'status', label: 'Status' },
        { key: 'usage', label: 'Usage' },
        { key: 'last_modified', label: 'Modified' },
        { key: 'actions', label: 'Actions' }
      ],

      // Table fields for automations
      automationTableFields: [
        { key: 'automation_info', label: 'Automation' },
        { key: 'trigger_type', label: 'Trigger' },
        { key: 'status', label: 'Status' },
        { key: 'steps_count', label: 'Steps' },
        { key: 'last_modified', label: 'Modified' },
        { key: 'actions', label: 'Actions' }
      ],


      // Copy to groups functionality
      groups: [],
      selectedGroups: [],
      selectedTemplate: null,
      selectedAutomation: null,
      modals: {
        copy_to_groups: false
      },
      copyType: '',
      errs: {
        groups: ''
      }
    }
  },

  computed: {
    // The SMS tab only exists for groups that finished carrier registration
    // and are in the SMS beta; everyone else sees the original two tabs.
    // Templates are authorable before carrier activation on purpose: a tenant
    // can write their messages while the campaign is still in review. Only
    // SENDING requires group.sms_active (enforced in sms-send-raw), so this
    // gates on the beta allowlist alone.
    smsEnabled() {
      return isSmsBetaCompany()
    },
    // TabRail addresses tabs by array position; the rest of this page uses the
    // stable `index` identity. Translate between them here.
    activeTabPosition() {
      const pos = this.tabs.findIndex(t => t.index === this.activeTabIndex)
      return pos === -1 ? 0 : pos
    },
    availableCategories() {
      const categories = [
        { key: 'all', name: 'All templates', icon: 'tim-icons icon-bullet-list-67', color: '#6c757d' }
      ]

      // Add categories from template types
      this.templateTypes.forEach(type => {
        const existing = categories.find(cat => cat.key === type.name.toLowerCase())
        if (!existing) {
          categories.push({
            key: type.name.toLowerCase(),
            name: type.name,
            icon: type.icon,
            color: type.color
          })
        }
      })

      return categories
    },

    tableFields() {
      return this.activeTabIndex === 0 ? this.templateTableFields : this.automationTableFields
    },

    displayData() {
      return this.activeTabIndex === 0 ? this.filteredTemplates : this.filteredAutomations
    },

    // The first-run hero is only for a group with no templates AND no automations at
    // all. The fetched lists are filtered, so an empty result there must not trigger it
    // (the hero has no filter bar, which strands a user behind a saved search).
    isBrandNewGroup() {
      return this.groupTemplateCount === 0 && this.groupAutomationCount === 0
    },

    // Unfiltered total for the active tab (null until the first fetch returns).
    activeTabGroupCount() {
      return this.activeTabIndex === 0 ? this.groupTemplateCount : this.groupAutomationCount
    },

    showEmptyState() {
      return [0, 1].includes(this.activeTabIndex) &&
        this.displayData.length === 0 &&
        this.activeTabGroupCount !== null &&
        !this.loading && !this.automationLoading
    },

    archiveDialogMessage() {
      const t = this.archiveDialog.template
      const subject = t ? `"${t.name}"` : 'This template'
      return `${subject} moves to the Archived filter. You can restore it from there as a draft.`
    },

    restoreDialogMessage() {
      const t = this.restoreDialog.template
      const subject = t ? `"${t.name}"` : 'This template'
      return `${subject} returns to your templates list as a draft. Activate it again when it is ready to send.`
    },

    // Filters other than the Archived switch that can hide templates.
    hasTemplateSearchFilters() {
      const f = this.filterValues
      const d = f.created_date || {}
      return !!f.name || (Array.isArray(f.template_type) && f.template_type.length > 0) ||
        !!f.status || !!d.from || !!d.to
    },

    // Records exist but the filters hide them all, vs. none created yet on this tab.
    emptyState() {
      const isTemplates = this.activeTabIndex === 0
      if (isTemplates && this.filterValues.archived === true) {
        return {
          title: 'No archived email templates',
          hint: this.hasTemplateSearchFilters
            ? 'Try a different search or clear the filters.'
            : 'Templates you archive show up here.'
        }
      }
      if (isTemplates && this.groupHasOnlyArchivedTemplates && !this.hasTemplateSearchFilters) {
        return {
          title: 'No active email templates',
          hint: 'Your templates are archived. Turn on Archived to see and restore them.'
        }
      }
      if (this.activeTabGroupCount > 0) {
        return {
          title: isTemplates ? 'No email templates match these filters' : 'No automations match these filters',
          hint: 'Try a different search or clear the filters.'
        }
      }
      return {
        title: isTemplates ? 'No email templates yet' : 'No automations yet',
        hint: isTemplates
          ? 'Design branded emails to send to your customers.'
          : 'Send emails to your customers automatically, at the right moment.'
      }
    },

    totalDisplayRows() {
      return this.activeTabIndex === 0 ? this.totalRows : this.totalAutomationRows
    },

    currentStatusOptions() {
      return this.activeTabIndex === 0 ? this.templateStatusOptions : this.automationStatusOptions
    },

    // Schema for the shared <filter-bar>. Tab-aware: template_type only applies to
    // the Templates tab, and the status options follow the active tab.
    filterSchema() {
      const isTemplates = this.activeTabIndex === 0
      const nameLabel = isTemplates ? 'Template name' : 'Automation name'
      const schema = [
        { key: 'name', type: 'text', label: nameLabel, placeholder: nameLabel, operatorLabel: 'contains', primary: true }
      ]
      if (isTemplates) {
        schema.push({
          key: 'template_type', type: 'multiselect', label: 'Template type', primary: true,
          options: (this.templateTypes || []).map(t => ({ label: t.name, value: t.name }))
        })
        // Switch on lists archived templates instead of active ones (OTC-3113)
        schema.push({ key: 'archived', type: 'toggle', label: 'Archived', primary: true })
      }
      schema.push({
        key: 'status', type: 'select', label: 'Status', primary: true,
        options: (this.currentStatusOptions || []).map(s => ({ label: s.label, value: s.value }))
      })
      schema.push({ key: 'created_date', type: 'daterange', label: 'Created date', primary: true })
      return schema
    }
  },

  created() {

    this.checkPermissionsAndBuildTabs();
    this.initializeFromQuery()
  },

  mounted() {
    // Initialize tab states (match on the tab's index value, not its array
    // position; permission filtering can make the two differ)
    this.tabs.forEach((tab) => {
      tab.active = tab.index === this.activeTabIndex
    })

    // FilterBar (saved-views enabled) resolves precedence and drives the first
    // fetch via its initial `change` (onFiltersChanged) — do not fetch here.
    // Seed the loading flag so the empty-state hero doesn't flash before it runs.
    if (this.activeTabIndex === 0) {
      this.loading = true
    } else {
      this.automationLoading = true
    }
  },

  methods: {
    checkPermissionsAndBuildTabs() {
      this.hasTemplatePermission = this.hasPermissions('custom_emails_templates_configure');
      this.hasAutomationPermission = this.hasPermissions('custom_emails_automation_configure');
      this.tabs = [];

      if (this.hasTemplatePermission) {
        this.tabs.push({
          name: 'Email templates',
          shortName: 'Email',
          icon: 'tim-icons icon-email-85',
          active: true,
          index: 0
        });
      }

      // Displayed between Email templates and Automation, but carries stable
      // index 2: `index` is identity, array position is display order (see the
      // mounted() comment). That keeps every activeTabIndex === 0 / === 1
      // check meaning email / automation regardless of whether SMS shows.
      if (this.hasTemplatePermission && this.smsEnabled) {
        this.tabs.push({
          name: 'SMS templates',
          shortName: 'SMS',
          icon: 'tim-icons icon-chat-33',
          active: this.tabs.length === 0,
          index: 2
        });
      }

      if (this.hasAutomationPermission) {
        this.tabs.push({
          name: 'Automation',
          shortName: 'Automation',
          icon: 'tim-icons icon-settings-gear-63',
          active: this.tabs.length === 0, // Active if it's the first tab
          index: 1
        });
      }


      if (this.tabs.length > 0) {
        const urlTab = Number(this.$route.query.tab);

        if (urlTab === 0 && this.hasTemplatePermission) {
          this.activeTabIndex = 0;
        } else if (urlTab === 1 && this.hasAutomationPermission) {
          this.activeTabIndex = 1;
        } else if (urlTab === 2 && this.hasTemplatePermission && this.smsEnabled) {
          this.activeTabIndex = 2;
        } else {
          this.activeTabIndex = this.tabs[0].index;
        }

        this.tabs.forEach(tab => {
          tab.active = tab.index === this.activeTabIndex;
        });
      }
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    getLocalDateTime(d) {
      if(!d) return ''
      const date = formatGroupDate(d, { dayOfWeek: false })
      const time = formatGroupTime(d)
      if(!date && !time) return ''
      return `${date}, ${time}`
    },

    async getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },

    // Initialize pagination/tab/category from URL query parameters. Filter values
    // are seeded separately in buildInitialFilterValues() for the shared FilterBar.
    initializeFromQuery() {
      const query = this.$route.query

      this.perPage = Number(query.count) || 10
      this.currentPage = Number(query.page) || 1
      this.activeCategory = query.category || 'all'
      this.activeTabIndex = Number(query.tab) || 0
    },

    // Seed the flat FilterBar values from the URL. Runs in data() (before created),
    // so $route.query is already available. template_type is a comma-joined list of
    // type names; created_date maps to the daterange from/to (range mode only).
    buildInitialFilterValues() {
      const q = this.$route.query || {}
      return {
        archived: q.archived === 'true',
        name: q.name || '',
        template_type: q.template_type ? String(q.template_type).split(',') : [],
        status: q.status || '',
        created_date: {
          mode: 'range',
          days: '',
          from: q.created_date_from || '',
          to: q.created_date_to || ''
        }
      }
    },

    // FilterBar change: apply new values and (re)fetch the active tab. The first
    // change is FilterBar's initial saved-views resolution and drives the first load.
    onFiltersChanged(values) {
      this.filterValues = {
        ...this.filterValues,
        ...values,
        // template_type is absent from the schema on the Automations tab; keep the
        // existing value rather than wiping it when the key isn't in the snapshot.
        template_type: values.template_type ? [...values.template_type] : this.filterValues.template_type,
        archived: values.archived !== undefined ? values.archived === true : this.filterValues.archived,
        created_date: values.created_date ? { ...values.created_date } : this.filterValues.created_date
      }
      if (!this.didInitialFetch) {
        this.didInitialFetch = true
        this.fetchActiveTab(false)
      } else {
        this.fetchActiveTab(true)
      }
    },

    onPerPageChange(n) {
      this.perPage = n
      this.fetchActiveTab(true)
    },

    // Fetch whichever tab is active. Resets to page 1 BEFORE computing the offset
    // (getOffset reads currentPage) so a stale page never produces a bad offset.
    fetchActiveTab(resetPage) {
      if (resetPage) {
        this.currentPage = 1
      }
      if (this.activeTabIndex === 0) {
        this.getTemplates(this.perPage, this.getOffset())
      } else {
        this.getAutomations(this.perPage, this.getOffset())
      }
    },

    startWalkthrough() {
      this.$router.push('/mail/custom/template')
    },

    showTemplateGallery() {
      this.$router.push({ path: '/mail/custom/template-types', query: { init: 1 }})
    },

    createNewAutomation() {
      this.$router.push('/mail/custom/automation')
    },

    proceedToBuilder() {
      this.$router.push(`/mail/custom/template`)
    },

    // TabRail hands back an array position; map it to the tab's stable index.
    onTabRailSelect(position) {
      const tab = this.tabs[position]
      if (tab) this.switchTab(tab.index)
    },

    // Tab switching
    async switchTab(index) {
      if ((index === 0 && !this.hasTemplatePermission) ||
        (index === 1 && !this.hasAutomationPermission)) {
        return;
      }
      this.activeTabIndex = index
      this.tabKey++
      this.currentPage = 1

      this.tabs.forEach((tab) => {
        tab.active = tab.index === index
      })

      if (index === 0) {
        // Templates tab
        this.getTemplates(this.perPage, this.getOffset())
      } else if (index === 1) {
        // Automations tab
        this.getAutomations(this.perPage, this.getOffset())
      }
    },

    getCategoryCount(categoryKey) {
      if (categoryKey === 'all') return this.templates.length
      return this.templates.filter(t => t.type.toLowerCase() === categoryKey).length
    },

    // Build query parameters for the templates API from the flat FilterBar values.
    buildQueryParams() {
      const params = new URLSearchParams()

      params.append('count', this.perPage)
      params.append('offset', this.getOffset())
      params.append('tab', this.activeTabIndex)

      // Add category filter
      if (this.activeCategory !== 'all') {
        params.append('category', this.activeCategory)
      }

      const f = this.filterValues
      if (f.name) params.append('search', f.name)
      if (Array.isArray(f.template_type) && f.template_type.length > 0) {
        params.append('template_type', f.template_type.join(','))
      }
      if (f.status) params.append('status', f.status)
      if (f.archived === true) params.append('archived', 'true')
      if (f.created_date && f.created_date.from) params.append('created_date_from', f.created_date.from)
      if (f.created_date && f.created_date.to) params.append('created_date_to', f.created_date.to)

      return params.toString()
    },

    // Build query parameters for the automations API from the flat FilterBar values.
    buildAutomationQueryParams(count, offset) {
      const params = new URLSearchParams()
      params.append('count', count)
      params.append('offset', offset)

      const f = this.filterValues
      if (f.name) params.append('search', f.name)
      if (f.status) params.append('status', f.status)
      if (f.created_date && f.created_date.from) params.append('created_date_from', f.created_date.from)
      if (f.created_date && f.created_date.to) params.append('created_date_to', f.created_date.to)

      return params.toString()
    },

    async getTemplates(count, offset) {
      this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }

        // Build query parameters
        const params = this.buildQueryParams()
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates?${params}`

        const response = await axios.get(url, config)
        this.templates = response.data.templates || []
        this.templateTypes = response.data.template_types || []
        this.totalRows = response.data.total_rows || 0

        this.filteredTemplates = this.templates
        // Any filtered row proves the group has templates; an empty page may only
        // mean the filters hide them all, so ask again unfiltered.
        if (this.totalRows > 0) {
          this.groupTemplateCount = this.totalRows
          if (this.filterValues.archived !== true) this.groupHasOnlyArchivedTemplates = false
        } else {
          this.groupTemplateCount = null
          await this.loadGroupTemplateCount(config)
        }
        if (this.groupTemplateCount === 0 && this.groupAutomationCount === null) {
          await this.loadGroupAutomationCount(config)
        }
        this.updateUrlQuery()
      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else {
          swal('Error!', 'Something went wrong', 'error')
        }
      } finally {
        this.loading = false
      }
    },

    // New method to fetch automations
    async getAutomations(count, offset) {
      this.automationLoading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }

        // Build query parameters for automations
        const params = this.buildAutomationQueryParams(count, offset)
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-automations?${params}`

        const response = await axios.get(url, config)
        this.automations = response.data.automations || []
        this.automationTriggerTypes = response.data.trigger_types || []
        this.totalAutomationRows = response.data.total_rows || 0

        this.filteredAutomations = this.automations
        this.groupAutomationCount = Number(response.data.status_counts?.total_count) || 0
        if (this.groupAutomationCount === 0 && this.groupTemplateCount === null) {
          await this.loadGroupTemplateCount(config)
        }
        this.updateUrlQuery()
      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else {
          console.error('Error fetching automations:', err)
          swal('Error!', 'Failed to load automations', 'error')
        }
      } finally {
        this.automationLoading = false
      }
    },

    // Unfiltered existence checks. A user without the permission can't see those
    // records, so they count as none. On any other error the total stays unknown
    // and the list view (with its filter bar) shows. The template check reads
    // total_rows, which comes from the same query as the list itself.
    async loadGroupAutomationCount(config) {
      if (!this.hasAutomationPermission) {
        this.groupAutomationCount = 0
        return
      }
      try {
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-automations?count=1&offset=0`
        const response = await axios.get(url, config)
        this.groupAutomationCount = Number(response.data.status_counts?.total_count) || 0
      } catch (err) {
        console.error('Error checking automations:', err)
      }
    },

    async loadGroupTemplateCount(config) {
      if (!this.hasTemplatePermission) {
        this.groupTemplateCount = 0
        return
      }
      try {
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates?count=1&offset=0`
        const response = await axios.get(url, config)
        let count = Number(response.data.total_rows) || 0
        // A group whose templates are all archived still has templates: keep the list
        // and its Archived switch reachable instead of showing the first-run hero.
        this.groupHasOnlyArchivedTemplates = false
        if (count === 0) {
          const archivedResponse = await axios.get(`${url}&archived=true`, config)
          count = Number(archivedResponse.data.total_rows) || 0
          this.groupHasOnlyArchivedTemplates = count > 0
        }
        this.groupTemplateCount = count
      } catch (err) {
        console.error('Error checking templates:', err)
      }
    },

    getOffset() {
      return Number((this.currentPage * this.perPage) - this.perPage)
    },

    // CSV Export
    async downloadCSV() {
      try {
        this.loading = true
        const allItems = this.activeTabIndex === 0 ? await this.getAllTemplates() : await this.getAllAutomations()

        const headers = this.activeTabIndex === 0 ? [
          'Name',
          'Description',
          'Type',
          'Status',
          'Sent Count',
          'Open Rate',
          'Click Rate',
          'Created',
          'Updated',
          'Created By'
        ] : [
          'Name',
          'Description',
          'Trigger Type',
          'Status',
          'Steps Count',
          'Created',
          'Updated',
          'Created By'
        ]

        const csvData = allItems.map(item => {
          if (this.activeTabIndex === 0) {
            return [
              item.name,
              item.description,
              item.type,
              item.status,
              item.sent_count,
              item.open_rate + '%',
              item.click_rate + '%',
              this.getLocalDateTime(item.created_at),
              this.getLocalDateTime(item.updated_at),
              item.created_by_name
            ]
          } else {
            return [
              item.name,
              item.description,
              item.trigger_type?.name || 'Unknown',
              this.getAutomationStatusText(item.is_active),
              item.steps_count,
              this.getLocalDateTime(item.created_at),
              this.getLocalDateTime(item.updated_at),
              item.created_by_name
            ]
          }
        })

        const filename = this.activeTabIndex === 0 ? 'email-templates.csv' : 'email-automations.csv'
        this.downloadCSVFile(csvData, headers, filename)
      } catch (error) {
        console.error('Error exporting data:', error)
        swal('Error!', 'Failed to export data', 'error')
      } finally {
        this.loading = false
      }
    },

    async getAllTemplates() {
      const token = await this.getAuthToken()
      const config = { headers: { Authorization: token } }
      const allTemplates = []
      let offset = 0
      const count = 1000

      while (offset <= this.totalRows) {
        const params = new URLSearchParams()
        params.append('count', count)
        params.append('offset', offset)

        // Add current filters
        const queryParams = this.buildQueryParams()
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates?${queryParams}`
        const response = await axios.get(url, config)

        allTemplates.push(...(response.data.templates || []))
        offset += count
      }

      return allTemplates
    },

    async getAllAutomations() {
      const token = await this.getAuthToken()
      const config = { headers: { Authorization: token } }
      const allAutomations = []
      let offset = 0
      const count = 1000

      while (offset <= this.totalAutomationRows) {
        const params = this.buildAutomationQueryParams(count, offset)
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-automations?${params}`
        const response = await axios.get(url, config)

        allAutomations.push(...(response.data.automations || []))
        offset += count
      }

      return allAutomations
    },

    // CSV download utility
    downloadCSVFile(data, headers, filename) {
      let csv = headers.join(',') + '\n'

      data.forEach(row => {
        csv += row.map(value => {
          if (typeof value === 'string') {
            value = value.replace(/"/g, '""')
            if (value.includes(',') || value.includes('\n')) {
              value = `"${value}"`
            }
          }
          return value || ''
        }).join(',') + '\n'
      })

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)

      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },

    updateUrlQuery() {
      const query = {
        count: this.perPage,
        page: this.currentPage,
        tab: this.activeTabIndex,
        category: this.activeCategory !== 'all' ? this.activeCategory : undefined
      }

      // Add filter parameters from the flat FilterBar values
      const f = this.filterValues
      if (f.name) query.name = f.name
      if (Array.isArray(f.template_type) && f.template_type.length > 0) {
        query.template_type = f.template_type.join(',')
      }
      if (f.status) query.status = f.status
      if (this.activeTabIndex === 0 && f.archived === true) query.archived = 'true'
      if (f.created_date && f.created_date.from) query.created_date_from = f.created_date.from
      if (f.created_date && f.created_date.to) query.created_date_to = f.created_date.to

      this.$router.replace({ query }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    },

    getStatusColor(status) {
      const colors = {
        active: 'success',
        draft: 'warning',
        paused: 'danger'
      }
      return colors[status] || 'secondary'
    },

    // Automation-specific helper methods
    getAutomationStatusColor(isActive) {
      return isActive ? 'success' : 'warning'
    },

    getAutomationStatusText(isActive) {
      return isActive ? 'Active' : 'Inactive'
    },

    getTriggerTypeIcon(triggerType) {
      const iconMap = {
        'booking': 'tim-icons icon-calendar-60',
        'payment': 'tim-icons icon-credit-card',
        'customer': 'tim-icons icon-single-02',
        'checklist': 'tim-icons icon-check-2',
        'staff': 'tim-icons icon-badge',
        'cart': 'tim-icons icon-cart',
        'transaction': 'tim-icons icon-money-coins',
        'gift_card': 'tim-icons icon-gift-2',
        'promo_code': 'tim-icons icon-tag',
        'inventory': 'tim-icons icon-box-2',
        'game': 'tim-icons icon-controller',
        'project': 'tim-icons icon-laptop',
        'survey': 'tim-icons icon-paper',
        'photo': 'tim-icons icon-camera-20',
        'waiver': 'tim-icons icon-paper',
        'system': 'tim-icons icon-settings-gear-63'
      }
      return iconMap[triggerType?.category] || 'tim-icons icon-bell-55'
    },

    getTypeGradient(color) {
      return `linear-gradient(135deg, ${color} 0%, ${color}88 100%)`
    },

    async duplicateTemplate(template) {
      this.loading = true

      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const requestBody = {}

        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates/${template.id}/duplicate`

        const response = await axios.post(url, requestBody, config)

        if (response.data && response.data.template) {
          const newTemplate = response.data.template

          // Show success message with options
          await swal({
            title: 'Success!',
            text: `Template "${template.name}" has been duplicated as "${newTemplate.name}"`,
            type: 'success',
            showCancelButton: true,
            confirmButtonText: 'Edit New Template',
            cancelButtonText: 'Stay Here'
          }).then((result) => {
            if (result.value) {
              // Navigate to edit the new template
              this.$router.push(`/mail/custom/template/${newTemplate.id}`)
            } else {
              // Refresh the template list to show the new duplicate
              this.getTemplates(this.perPage, this.getOffset())
            }
          })
        } else {
          // If no template data in response, just refresh the list
          swal('Success!', 'Template has been duplicated.', 'success')
          this.getTemplates(this.perPage, this.getOffset())
        }

      } catch (err) {
        this.loading = false

        if (err.response) {
          if (err.response.status === 401) {
            this.$router.push('/forbidden')
          } else if (err.response.status === 404) {
            swal('Error!', 'Template not found', 'error')
          } else if (err.response.status === 400 && err.response.data) {
            // Handle validation errors
            let errorMessage = 'Failed to duplicate template'

            if (err.response.data.error) {
              errorMessage = err.response.data.error
            } else if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
              errorMessage = err.response.data.errors.join('\n')
            }

            swal('Validation Error', errorMessage, 'error')
          } else {
            console.error('Error duplicating template:', err)
            swal('Error!', 'Failed to duplicate template. Please try again.', 'error')
          }
        } else {
          console.error('Error duplicating template:', err)
          swal('Error!', 'An unexpected error occurred. Please try again.', 'error')
        }
      } finally {
        this.loading = false
      }
    },
    viewAnalytics(template) {
      this.$router.push(`/email-templates/${template.id}/analytics`)
    },

    openArchiveDialog(template) {
      this.archiveDialog = { show: true, loading: false, template }
    },

    async archiveTemplate() {
      const template = this.archiveDialog.template
      this.archiveDialog.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates/${template.id}`
        await axios.delete(url, config)
        this.archiveDialog.show = false
        this.$notify({ type: 'success', message: 'Template archived', icon: 'tim-icons icon-check-2' })
        await this.refreshTemplatesAfterRowRemoval()
      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else {
          this.$notify({ type: 'danger', message: 'Something went wrong. Please try again.', icon: 'tim-icons icon-alert-circle-exc' })
        }
      } finally {
        this.archiveDialog.loading = false
      }
    },

    openRestoreDialog(template) {
      this.restoreDialog = { show: true, loading: false, template }
    },

    async restoreTemplate() {
      const template = this.restoreDialog.template
      this.restoreDialog.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates/${template.id}`
        await axios.put(url, { archived: false }, config)
        this.restoreDialog.show = false
        this.$notify({ type: 'success', message: 'Template restored as a draft', icon: 'tim-icons icon-check-2' })
        await this.refreshTemplatesAfterRowRemoval()
      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else if (err.response?.status === 409 && err.response?.data?.error) {
          this.restoreDialog.show = false
          this.$notify({ type: 'warning', message: err.response.data.error, icon: 'tim-icons icon-alert-circle-exc' })
        } else if (err.response?.status === 404) {
          this.restoreDialog.show = false
          this.$notify({ type: 'warning', message: 'This template was already restored or no longer exists.', icon: 'tim-icons icon-alert-circle-exc' })
          await this.refreshTemplatesAfterRowRemoval()
        } else {
          this.$notify({ type: 'danger', message: 'Something went wrong. Please try again.', icon: 'tim-icons icon-alert-circle-exc' })
        }
      } finally {
        this.restoreDialog.loading = false
      }
    },

    // After a row leaves the list (archived or restored), step back a page when it
    // was the last row on a page past the first, then reload.
    async refreshTemplatesAfterRowRemoval() {
      if (this.displayData.length === 1 && this.currentPage > 1) {
        // b-pagination's v-model watcher emits input on this change and its @input
        // handler reloads the list, so fetching here as well would load it twice.
        this.currentPage -= 1
        return
      }
      await this.getTemplates(this.perPage, this.getOffset())
    },
    async toggleTemplateStatus(template) {
      const newStatus = template.status === 'active' ? 'draft' : 'active'

      // Activating requires a sending domain that can actually deliver. The list
      // payload carries the verdict so we can stop before the round trip.
      if (newStatus === 'active' && template.can_activate === false) {
        swal({
          title: 'Cannot activate',
          text: template.activation_block_reason || 'This template cannot be activated until its from email uses a verified sending domain.',
          type: 'warning'
        })
        return
      }

      this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates/${template.id}`

        await axios.put(url, { status: newStatus }, config)
        this.loading = false
        template.status = newStatus
        swal('Success!', `Template ${newStatus === 'active' ? 'activated' : 'deactivated'}.`, 'success')
      } catch (err) {
        this.loading = false
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else if (err.response?.status === 400 && err.response?.data?.error) {
          swal('Cannot activate', err.response.data.error, 'warning')
        } else {
          swal('Error!', 'Something went wrong', 'error')
        }
      }
    },
    async duplicateAutomation(automation) {
      this.automationLoading = true

      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const requestBody = {}
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-automations/${automation.id}/duplicate`

        const response = await axios.post(url, requestBody, config)

        if (response.data && response.data.automation) {
          const newAutomation = response.data.automation

          let successMessage = `Successfully created "${newAutomation.name}"`

          if (response.data.warnings && response.data.warnings.length > 0) {
            successMessage += '\n\nWarnings:\n' + response.data.warnings.join('\n')
          }

          await swal({
            title: 'Success!',
            text: successMessage,
            type: 'success',
            confirmButtonText: 'View Automation',
            showCancelButton: true,
            cancelButtonText: 'Stay Here'
          }).then((viewResult) => {
            if (viewResult.value) {
              this.$router.push(`/mail/custom/automation/${newAutomation.id}`)
            } else {
              this.getAutomations(this.perPage, this.getOffset())
            }
          })
        } else {
          this.getAutomations(this.perPage, this.getOffset())
          swal('Success!', 'Automation duplicated successfully', 'success')
        }

      } catch (err) {
        this.automationLoading = false

        if (err.response) {
          if (err.response.status === 401) {
            this.$router.push('/forbidden')
          } else if (err.response.status === 404) {
            swal('Error!', 'Automation not found', 'error')
          } else if (err.response.status === 400 && err.response.data) {
            let errorMessage = 'Failed to duplicate automation'

            if (err.response.data.error) {
              errorMessage = err.response.data.error
            } else if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
              errorMessage = err.response.data.errors.join('\n')
            }

            swal('Validation Error', errorMessage, 'error')
          } else {
            console.error('Error duplicating automation:', err)
            swal('Error!', 'Failed to duplicate automation. Please try again.', 'error')
          }
        } else {
          console.error('Error duplicating automation:', err)
          swal('Error!', 'An unexpected error occurred. Please try again.', 'error')
        }
      } finally {
        this.automationLoading = false
      }
    },
    async toggleAutomationStatus(automation) {
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-automations/${automation.id}/toggle`

        await axios.put(url, {}, config)
        swal('Success!', `Automation ${automation.is_active ? 'paused' : 'activated'}.`, 'success')
        this.getAutomations(this.perPage, this.getOffset())
      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else {
          swal('Error!', 'Something went wrong', 'error')
        }
      }
    },

    viewAutomationAnalytics(automation) {
      this.$router.push(`/email-automations/${automation.id}/analytics`)
    },

    deleteAutomationPrompt(automation) {
      swal({
        title: 'Are you sure?',
        text: `This will permanently delete the automation "${automation.name}".`,
        type: 'warning',
        showCancelButton: true
      }).then((response) => {
        if (response.value) {
          this.deleteAutomation(automation)
        }
      })
    },

    async deleteAutomation(automation) {
      this.automationLoading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-automations/${automation.id}`

        await axios.delete(url, config)
        swal('Deleted!', 'Automation has been deleted.', 'success')
        this.getAutomations(this.perPage, this.getOffset())
      } catch (err) {
        this.automationLoading = false
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else {
          swal('Error!', 'Something went wrong', 'error')
        }
      }
    },

    // Copy to groups methods
    async getGroups() {
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups'

      try {
        const response = await axios.get(url, config)
        this.groups = response.data.filter(
          group => group.id !== this.companyGroupId
        )
      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else {
          swal('Error!', 'Failed to load groups', 'error')
        }
      }
    },

    async copyToGroupsPrompt(item, type) {
      this.copyType = type
      this.selectedGroups = []
      this.errs.groups = ''

      if (type === 'template') {
        this.selectedTemplate = item
        this.selectedAutomation = null
      } else {
        this.selectedAutomation = item
        this.selectedTemplate = null
      }

      await this.getGroups()
      this.modals.copy_to_groups = true
    },

    async executeCopy() {
      if (this.selectedGroups.length <= 0) {
        this.errs.groups = 'Please select at least one group'
        return
      }
      this.errs.groups = ''

      if (this.copyType === 'template') {
        await this.copyTemplateToGroups()
      } else {
        await this.copyAutomationToGroups()
      }
    },

    async copyTemplateToGroups() {
      this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates/${this.selectedTemplate.id}/copy-to-groups`

        const data = {
          target_group_ids: this.selectedGroups.map(g => g.id)
        }

        const response = await axios.post(url, data, config)

        let message = `Template "${this.selectedTemplate.name}" has been copied to ${this.selectedGroups.length} group(s)`
        if (response.data.warnings && response.data.warnings.length > 0) {
          message += '\n\nWarnings:\n' + response.data.warnings.join('\n')
        }

        swal('Success!', message, 'success')
        this.modals.copy_to_groups = false
        this.selectedGroups = []

      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else if (err.response?.data?.error) {
          swal('Error!', err.response.data.error, 'error')
        } else {
          swal('Error!', 'Failed to copy template', 'error')
        }
      } finally {
        this.loading = false
      }
    },

    async copyAutomationToGroups() {
      this.automationLoading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-automations/${this.selectedAutomation.id}/copy-to-groups`

        const data = {
          target_group_ids: this.selectedGroups.map(g => g.id)
        }

        const response = await axios.post(url, data, config)

        let message = `Automation "${this.selectedAutomation.name}" has been copied to ${this.selectedGroups.length} group(s)`
        if (response.data.warnings && response.data.warnings.length > 0) {
          message += '\n\nWarnings:\n' + response.data.warnings.join('\n')
        }

        swal('Success!', message, 'success')
        this.modals.copy_to_groups = false
        this.selectedGroups = []

      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else if (err.response?.data?.error) {
          swal('Error!', err.response.data.error, 'error')
        } else {
          swal('Error!', 'Failed to copy automation', 'error')
        }
      } finally {
        this.automationLoading = false
      }
    }
  }
}
</script>

<style scoped>
/* Hero Section Styles */
.hero-container {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  padding: 2rem 2rem;
  grid-template-columns: 1fr 500px;
  gap: 3rem;
  align-items: center;
}

.hero-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 1rem;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.hero-description {
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2rem;
  line-height: 1.6;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.demo-preview {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 0.5rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Category Tabs */
.category-tabs {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.5rem;
  background: #f8fafc;
  border-radius: 0.75rem;
}

.category-tab {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  border: 1px solid transparent;
}

.category-tab:hover {
  background: #f1f5f9;
  color: #374151;
}

.category-tab.active {
  background: #3b82f6;
  color: white;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
}

.tab-badge {
  background: rgba(255, 255, 255, 0.2);
  color: inherit;
  padding: 0.125rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.category-tab:not(.active) .tab-badge {
  background: #e5e7eb;
  color: #6b7280;
}

/* Filter styles from PaymentList */
.pagination-select {
  min-width: 80px;
}

.font-icon-detail {
  text-align: center;
  padding: 2rem;
}

.popover-header {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
}

.popover-body {
  min-width: 250px;
}

.text-dark {
  color: #333 !important;
}

.text-dark label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
}

/* Grid View */
.template-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: hidden;
  transition: all 0.2s;
  cursor: pointer;
}

.template-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: #3b82f6;
}

.template-preview {
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
}

.preview-content i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.template-info {
  padding: 1.5rem;
}

.template-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #111827;
}

.template-description {
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.template-stats {
  margin-bottom: 1rem;
}

.template-actions {
  display: flex;
  gap: 0.5rem;
}

/* Template Type Selection */
.template-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.template-type-option {
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.template-type-option:hover {
  border-color: #3b82f6;
  transform: translateY(-2px);
}

.template-type-option.selected {
  border-color: #3b82f6;
  background: #eff6ff;
}

.template-type-option i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.template-type-option h5 {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #111827;
}

.template-type-option p {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

/* Mobile */
.mobile-template-card {
  padding: 0.5rem 0;
}

/* Enhanced Demo Interface Styles - Dark Theme */
.demo-interface-enhanced {
  display: grid;
  grid-template-columns: 140px 1fr 160px;
  gap: 0;
  background: #22233a;
  border-radius: 0.75rem;
  overflow: hidden;
  min-height: 350px;
  box-shadow: 0 8px 26px -4px rgba(20, 20, 43, 0.15), 0 8px 9px -5px rgba(20, 20, 43, 0.06);
}

/* Enhanced Sidebar - Dark Theme */
.demo-sidebar {
  background: #171826;
  padding: 1rem;
  color: #ffffff;
  border-right: 1px solid #393c54;
}

.demo-sidebar h4 {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.5px;
}

.component-grid {
  display: grid;
  gap: 0.375rem;
  margin-bottom: 1.5rem;
}

.component-item {
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  padding: 0.5rem;
  text-align: center;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.8);
  cursor: grab;
  transition: all 0.2s;
}

.component-item:hover {
  border-color: #e14eca;
  transform: translateY(-1px);
  box-shadow: 0 5px 10px 0 rgba(225, 78, 202, 0.3);
  background: #2b2c40;
}

.component-item.draggable {
  animation: float 3s ease-in-out infinite;
  border-color: #e14eca;
  box-shadow: 0 2px 8px 0 rgba(225, 78, 202, 0.2);
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
}

.component-item i {
  display: block;
  margin-bottom: 0.25rem;
  color: #e14eca;
  font-size: 0.875rem;
}

.feature-highlight {
  background: linear-gradient(45deg, #e14eca, #ba54f5);
  border-radius: 0.5rem;
  padding: 0.75rem;
  color: white;
  font-size: 0.7rem;
  box-shadow: 0 4px 20px 0 rgba(225, 78, 202, 0.4);
}

.highlight-icon {
  text-align: center;
  margin-bottom: 0.5rem;
}

.highlight-icon i {
  font-size: 1.25rem;
}

.highlight-text h5 {
  margin: 0 0 0.25rem 0;
  font-size: 0.75rem;
  font-weight: 600;
}

.highlight-text p {
  margin: 0;
  opacity: 0.9;
  font-size: 0.65rem;
  line-height: 1.3;
}

/* Enhanced Canvas - Dark Theme */
.demo-canvas-enhanced {
  background: #22233a;
  display: flex;
  flex-direction: column;
}

.canvas-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #171826;
  border-bottom: 1px solid #393c54;
}

.canvas-tabs {
  display: flex;
  gap: 0.5rem;
}

.tab {
  padding: 0.375rem 0.75rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.2s;
}

.tab.active {
  background: linear-gradient(45deg, #e14eca, #ba54f5);
  color: white;
  border-color: #e14eca;
  box-shadow: 0 4px 20px 0 rgba(225, 78, 202, 0.4);
}

.canvas-tools {
  display: flex;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
}

.canvas-tools i {
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: all 0.2s;
}

.canvas-tools i:hover {
  background: #393c54;
  color: #e14eca;
}

.canvas-content {
  flex: 1;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Animation Area - Dark Theme */
.demo-animation-area {
  margin-bottom: 1rem;
}

.drag-demo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.component-source {
  flex: 0 0 auto;
}

.component-block.floating {
  background: #171826;
  border: 2px dashed #e14eca;
  border-radius: 0.375rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.8);
  cursor: grab;
  animation: componentFloat 2s ease-in-out infinite;
  box-shadow: 0 2px 8px 0 rgba(225, 78, 202, 0.3);
}

@keyframes componentFloat {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-4px) rotate(1deg); }
}

.drag-path {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.drag-arrow {
  font-size: 1.25rem;
  color: #e14eca;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
}

.drop-zone {
  flex: 0 0 auto;
}

.canvas-preview {
  border: 2px dashed #e14eca;
  border-radius: 0.5rem;
  padding: 0.75rem;
  background: rgba(225, 78, 202, 0.1);
  width: 120px;
}

.email-structure {
  background: #171826;
  border-radius: 0.25rem;
  overflow: hidden;
  font-size: 0.65rem;
  border: 1px solid #393c54;
}

.email-header-preview,
.email-footer-preview {
  background: #393c54;
  padding: 0.25rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
}

.email-content-preview {
  padding: 0.5rem 0.25rem;
  min-height: 60px;
}

.content-block {
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.25rem;
  padding: 0.375rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.7);
}

.content-block.added {
  background: rgba(225, 78, 202, 0.2);
  border-color: #e14eca;
  color: #ffffff;
  animation: highlight 2s ease-in-out;
  box-shadow: 0 2px 4px 0 rgba(225, 78, 202, 0.3);
}

@keyframes highlight {
  0% { background: rgba(225, 78, 202, 0.2); }
  50% { background: rgba(225, 78, 202, 0.4); }
  100% { background: rgba(225, 78, 202, 0.2); }
}

.drop-indicator {
  height: 2px;
  background: #e14eca;
  border-radius: 1px;
  animation: blink 1.5s ease-in-out infinite;
  box-shadow: 0 0 8px #e14eca;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.demo-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  text-align: center;
  padding: 0.75rem;
  background: #171826;
  border-radius: 0.5rem;
  border: 1px solid #393c54;
}

.demo-hint i {
  color: #fd5d93;
}

/* Properties Panel - Dark Theme */
.demo-properties {
  background: #171826;
  padding: 1rem;
  border-left: 1px solid #393c54;
  font-size: 0.75rem;
  color: #ffffff;
}

.demo-properties h4 {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.5px;
}

.property-group {
  margin-bottom: 1rem;
}

.property-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.property-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.property-input input[type="range"] {
  flex: 1;
  height: 4px;
  background: #393c54;
  border-radius: 2px;
  outline: none;
  -webkit-appearance: none;
}

.property-input input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  background: #e14eca;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(225, 78, 202, 0.4);
}

.property-input span {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  min-width: 30px;
}

.color-picker {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.25rem;
}

.color-swatch {
  width: 20px;
  height: 20px;
  border-radius: 0.25rem;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.color-swatch:hover,
.color-swatch.active {
  border-color: #e14eca;
  transform: scale(1.1);
  box-shadow: 0 2px 4px rgba(225, 78, 202, 0.4);
}

.alignment-buttons {
  display: flex;
  gap: 0.25rem;
}

.align-btn {
  padding: 0.375rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s;
  color: rgba(255, 255, 255, 0.7);
}

.align-btn:hover,
.align-btn.active {
  background: #e14eca;
  color: white;
  border-color: #e14eca;
  box-shadow: 0 4px 20px 0 rgba(225, 78, 202, 0.4);
}

/* Template Stats - Dark Theme */
.template-stats {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #393c54;
}

.template-stats h5 {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.5px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.65rem;
}

.stat-item span:first-child {
  flex: 0 0 60px;
  color: rgba(255, 255, 255, 0.8);
}

.stat-bar {
  flex: 1;
  height: 6px;
  background: #393c54;
  border-radius: 3px;
  overflow: hidden;
}

.stat-fill {
  height: 100%;
  background: linear-gradient(90deg, #e14eca, #ba54f5);
  transition: width 2s ease-out;
  animation: fillBar 3s ease-out;
  box-shadow: 0 0 8px rgba(225, 78, 202, 0.4);
}

@keyframes fillBar {
  from { width: 0%; }
  to { width: var(--width); }
}

.stat-item span:last-child {
  flex: 0 0 30px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
}

/* Responsive */
@media (max-width: 1024px) {
  .hero-container {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .demo-interface-enhanced {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }

  .category-tabs {
    overflow-x: scroll;
  }

  .template-types-grid {
    grid-template-columns: 1fr;
  }

  .hero-actions {
    flex-direction: column;
    align-items: center;
  }

  .d-flex.justify-content-between.flex-wrap > * {
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 576px) {
  .template-card {
    margin-bottom: 0.75rem;
  }
}


/* Tab header: same pattern as CustomFieldsLibrary — the hairline lives on the
   flex container so it spans past the action button, and TabRail's own border
   is suppressed. */
.tabs-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.tabs-header ::v-deep .tab-rail {
  border-bottom-color: transparent;
  min-width: 0;
}
.tabs-header__action {
  margin-top: 0;
  margin-bottom: 14px;
  margin-left: 1rem;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.white-content .tabs-header {
  border-bottom-color: rgba(0, 0, 0, 0.12);
}
</style>
